import { useCallback, useState } from "react";

interface UseDefinitionJsonManagerArgs {
  serverDefinition: Record<string, unknown> | undefined;
}

interface UseDefinitionJsonManagerReturn {
  rawJson: Record<string, unknown>;
  workingJson: Record<string, unknown>;
  hasChanges: boolean;

  // JSON editor edits: update editorJsonString; workingJson updates only when valid JSON
  updateEditorString: (jsonString: string) => void;

  // Template application: replace workingJson entirely
  applyTemplate: (templateDefinition: Record<string, unknown>) => void;

  // Re-initialize from server data (called when server fetch completes)
  initFromServer: (definition: Record<string, unknown>) => void;

  // Revert workingJson to rawJson
  revertToRaw: () => void;

  // String representation for Monaco editor
  editorJsonString: string;

  // Whether editorJsonString is currently valid JSON
  isEditorJsonValid: boolean;
}

export function useDefinitionJsonManager({
  serverDefinition,
}: UseDefinitionJsonManagerArgs): UseDefinitionJsonManagerReturn {
  const initialJson = serverDefinition ?? {};

  const [rawJson, setRawJson] = useState<Record<string, unknown>>(initialJson);
  const [workingJson, setWorkingJson] = useState<Record<string, unknown>>(initialJson);
  const [editorJsonString, setEditorJsonString] = useState<string>(JSON.stringify(initialJson, null, 2));
  const [isEditorJsonValid, setIsEditorJsonValid] = useState<boolean>(true);

  const [rawJsonString, setRawJsonString] = useState<string>(JSON.stringify(initialJson, null, 2));

  const hasChanges = editorJsonString !== rawJsonString;

  const updateEditorString = useCallback((jsonString: string) => {
    setEditorJsonString(jsonString);
    try {
      const parsed = JSON.parse(jsonString) as Record<string, unknown>;
      setWorkingJson(parsed);
      setIsEditorJsonValid(true);
    } catch {
      setIsEditorJsonValid(false);
    }
  }, []);

  const applyTemplate = useCallback((templateDefinition: Record<string, unknown>) => {
    setWorkingJson(templateDefinition);
    setEditorJsonString(JSON.stringify(templateDefinition, null, 2));
  }, []);

  const initFromServer = useCallback((definition: Record<string, unknown>) => {
    const jsonStr = JSON.stringify(definition, null, 2);
    setRawJson(definition);
    setRawJsonString(jsonStr);
    setWorkingJson(definition);
    setEditorJsonString(jsonStr);
    setIsEditorJsonValid(true);
  }, []);

  const revertToRaw = useCallback(() => {
    setWorkingJson(rawJson);
    setEditorJsonString(JSON.stringify(rawJson, null, 2));
    setIsEditorJsonValid(true);
  }, [rawJson]);

  return {
    rawJson,
    workingJson,
    hasChanges,
    initFromServer,
    updateEditorString,
    applyTemplate,
    revertToRaw,
    editorJsonString,
    isEditorJsonValid,
  };
}
